import React, { useState, useEffect } from "react"
import styled, { css } from "styled-components"
import { motion } from "framer-motion"
import { GatsbyImage } from "gatsby-plugin-image"

import useWindowSize from "@hooks/use-window-size"
import Icon from "./icon"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const Wrapper = styled.div`
  overflow: hidden;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Container = styled.div`
  max-width: 1248px;
  padding: 32px 48px 40px;
  margin: 0 auto;
  ${below.mobile`
  padding: 0 24px;
  `}
  h2 {
    font-family: "Playfair Display", serif;
    font-size: 40px;
    line-height: 56px;
    font-weight: 400;
    margin-bottom: 12px;
    ${below.tablet`
  font-size: 32px;
  line-height: 48px;
  `}
    ${below.mobile`
  font-size: 28px;
  line-height: 40px;
  `}
  }
  .arrow {
    background-color: transparent;
    width: 24px;
    height: 24px;
    padding: 5px;
    border: none;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    margin-left: 20px;
    stroke: black;
    transition: 0.3s ease-in-out;
    &:hover {
      background-color: #e2e8f0;
    }
    &.inactive {
      stroke: #cbd2dd;
      pointer-events: none;
    }
  }
`
const Track = styled(motion.div)`
  min-width: ${props => props.$trackWidth || "1920"}px;
  max-height: 352px;
  overflow: hidden;
  display: flex;
  margin-left: -12px;

  cursor: grab;
  &:active {
    cursor: grabbing;
  }
  .slide {
    flex: 1;
    padding: 12px;
    ${below.mobile`
  max-width: 304px;
  min-height: 352px;
  `}
  }
`

const FramerSlide = props => {
  const [index, setIndex] = useState(0)
  const [slideWidth, setSlideWidth] = useState(480)
  const slideCount = props.slides.length
  const windowSize = useWindowSize()
  const gutters = windowSize.width > 600 ? 72 : 24
  const windowWidth =
    windowSize.width <= 1248 ? windowSize.width - gutters : 1248 - gutters
  const trackLength = slideCount * slideWidth
  const dragLimit = (trackLength - windowWidth) * -1
  const slide = direction => {
    if (direction === "forward") {
      if (index - slideWidth > dragLimit) {
        setIndex(index - slideWidth)
      } else {
        setIndex(dragLimit)
      }
    } else {
      if (index + slideWidth < 0) {
        setIndex(index + slideWidth)
      } else {
        setIndex(0)
      }
    }
  }

  useEffect(() => {
    windowSize.width > 600 ? setSlideWidth(480) : setSlideWidth(304)
  }, [windowSize.width, slideWidth])

  return (
    <>
      <Wrapper>
        <Container>
          <FlexContainer>
            <h2>This is my story.</h2>
            <div>
              <button
                className={index >= 0 ? "inactive arrow" : "arrow"}
                onClick={() => slide("back")}
              >
                <Icon icon="arrow-left" />
              </button>
              <button
                className={index <= dragLimit ? "inactive arrow" : "arrow"}
                onClick={() => slide("forward")}
              >
                <Icon icon="arrow-right" />
              </button>
            </div>
          </FlexContainer>

          <Track
            $trackWidth={trackLength}
            initial={{ x: "0px" }}
            animate={{ x: index }}
            transition={{ duration: 0.4 }}
            drag="x"
            dragConstraints={{ left: dragLimit, right: 0 }}
            dragElastic={0.3}
            onDragEnd={(event, info) => {
              if (info.point.x > slideWidth * -1) {
                setIndex(0)
              }
              if (
                info.point.x <= slideWidth * -1 &&
                info.point.x > slideWidth * -2
              ) {
                setIndex(slideWidth * -1)
              }
              if (
                info.point.x <= slideWidth * -2 &&
                info.point.x > slideWidth * -3
              ) {
                setIndex(slideWidth * -2)
              }
              if (
                info.point.x <= slideWidth * -3 &&
                info.point.x > slideWidth * -4
              ) {
                setIndex(slideWidth * -3)
              }
              if (
                info.point.x <= slideWidth * -4 &&
                info.point.x > slideWidth * -5
              ) {
                setIndex(dragLimit)
              }
            }}
          >
            {props.slides.map(slide => {
              return (
                <div className="slide" key={slide.id}>
                  {slide.description !== "" ? (
                    <a
                      href={slide.description}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <GatsbyImage
                        image={slide.gatsbyImageData}
                        objectFit="cover"
                        objectPosition="50% 50%"
                        style={{ height: "100%" }}
                        imgStyle={{
                          pointerEvents: "none",
                        }}
                        alt={""}
                      />
                    </a>
                  ) : (
                    <GatsbyImage
                      image={slide.gatsbyImageData}
                      objectFit="cover"
                      objectPosition="50% 50%"
                      style={{ height: "100%" }}
                      imgStyle={{
                        pointerEvents: "none",
                      }}
                      alt={""}
                    />
                  )}
                </div>
              )
            })}
          </Track>
        </Container>
      </Wrapper>
    </>
  )
}

export default FramerSlide
